import {
  NOTIFICATION_TYPES,
  DRAWER_PLACEMENTS,
} from "../../../../../config/constants";
import {
  Select,
  Button,
  Divider,
  Menu,
  Empty,
  Spin,
  Table,
  Space,
  Drawer,
  Form,
  Input,
  message,
  Popconfirm,
  UploadFile,
  AutoComplete,
} from "antd";
import "../TableSetup.css";
import {
  CopyOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import styles from "../../table-setup/TableSetup.module.css";
import { ColumnsType } from "antd/lib/table";
import TranslationService from "../../../../../services/translations/TranslationsService";
import { CreateTranslationModel } from "../../../../../models/translations/CreateTranslationModel";
import { UpdateTranslationModel } from "../../../../../models/translations/UpdateTranslationModel";
import Upload, { UploadChangeParam } from "antd/lib/upload";
import saveAs from "file-saver";
import { useTranslation } from "react-i18next";
import NotificationsUtil from "../../../../shared/utils/NotificationsUtil";
import Datatable from "../../../../shared/components/Datatable";

enum RowAction {
  View = 1,
  Edit,
  Create,
  Download,
  Upload,
}

const TableSetup = ({
  groupCodes,
  selectedGroupCode,
  newTranslationDrawerEnabled,
  setNewTranslationDrawerEnabled,
  downloadTranslationDrawerEnabled,
  setDownloadTranslationDrawerEnabled,
  downloadTranslationTemplateDrawerEnabled,
  setDownloadTranslationTemplateDrawerEnabled,
  uploadTranslationDrawerEnabled,
  setUploadTranslationDrawerEnabled,
}: {
  groupCodes: any;
  selectedEntity: any;
  selectedGroupCode: string;
  selectedOperation: any;
  newTranslationDrawerEnabled: boolean;
  setNewTranslationDrawerEnabled: (val: boolean) => void;
  downloadTranslationDrawerEnabled: boolean;
  setDownloadTranslationDrawerEnabled: (val: boolean) => void;
  downloadTranslationTemplateDrawerEnabled: boolean;
  setDownloadTranslationTemplateDrawerEnabled: (val: boolean) => void;
  uploadTranslationDrawerEnabled: boolean;
  setUploadTranslationDrawerEnabled: (val: boolean) => void;
}) => {
  const { t, i18n } = useTranslation("translations");
  const [drawerForm] = Form.useForm();
  const { Option } = Select;
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [translationKey, setTranslationKey] = useState<any>([]);
  const [translationAttributes, setTranslationAttributes] = useState<any>([]);
  const [allTranslationAttributes, setAllTranslationAttributes] = useState<any>(
    []
  );
  const [drawerTranslationAttributes, setDrawerTranslationAttributes] =
    useState<any>([]);
  const [attributeInput, setAttributeInput] = useState("");
  const [fromValueInput, setFromValueInput] = useState("");
  const [toValueInput, setToValueInput] = useState("");

  const [selectedAttribute, setSelectedAttribute] = useState<any>("");
  const [selectedTranslationGroupCode, setSelectedTranslationGroupCode] =
    useState<any>(selectedGroupCode);

  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerAction, setDrawerAction] = useState<RowAction>();

  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);
  const [selectedTranslationsFile, setSelectedTranslationsFile] =
    useState<any>(null);

  let locale = {
    emptyText: [
      <Empty
        description={
          !selectedAttribute &&
          t(
            "Page.Configuration.TableSetup.Tabs.Translations.Tutorial.GetStarted",
            {
              lngs: i18n.languages,
              defaultValue:
                "Select a Translation key on the left to get started",
            }
          )
        }
        image={Empty.PRESENTED_IMAGE_DEFAULT}
      />,
    ],
  };

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    
    const items = selectedAttribute?.split(" - ") ?? "";
    
    const translationGroupCode = selectedGroupCode ? selectedGroupCode : 
        (items.length === 1 ? 
          null : items[0]);
    const translationAttribute = selectedGroupCode ? selectedAttribute : (items.length === 1 ? items[0] : items[1]);
    
    setSelectedTranslationGroupCode(translationGroupCode);
    setAttributeInput(translationAttribute);
    drawerForm.setFieldsValue({
      groupCode: translationGroupCode,
      attribute: translationAttribute
    });
    
    switch (action) {
      case RowAction.View:
        setDrawerTitle("Row details");
        break;
      case RowAction.Create:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.Translations.Labels.NewTranslation",
            { lngs: i18n.languages, defaultValue: "New translation" }
          )!
        );
        break;
      case RowAction.Edit:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.Translations.Labels.EditTranslation",
            { lngs: i18n.languages, defaultValue: "Edit translation" }
          )!
        );
        break;
      case RowAction.Upload:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.Translations.Labels.UploadTranslations",
            { lngs: i18n.languages, defaultValue: "Upload translation" }
          )!
        );
        break;
      case RowAction.Download:
        setDrawerTitle(
          t(
            "Page.Configuration.TableSetup.Tabs.Translations.Labels.DownloadTranslations",
            { lngs: i18n.languages, defaultValue: "Download translations" }
          )!
        );
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onDoubleClickRow = (record: any) => {
    showDrawer(RowAction.View);
    setSelectedTranslationGroupCode(record.groupCode);
    setAttributeInput(record.attribute);
    setFromValueInput(record.fromValue);
    setToValueInput(record.toValue);
    setEditedRow(record);

    drawerForm.setFieldsValue({ groupCode: record.groupCode });
  };

  const onClose = () => {
    setOpenDrawer(false);
    setAttributeInput("");
    setFromValueInput("");
    setToValueInput("");
    setEditedRow(null);
    setSelectedTranslationGroupCode("");
    setNewTranslationDrawerEnabled(false);
    setDownloadTranslationDrawerEnabled(false);
    setUploadTranslationDrawerEnabled(false);
    setSelectedTranslationsFile(null);
  };

  const handleTranslationsFileChange = (
    value: UploadChangeParam<UploadFile>
  ) => {
    if (value.fileList.length === 0) {
      setSelectedTranslationsFile(null);
    } else {
      setSelectedTranslationsFile(value?.fileList[0].originFileObj);
      // const file: any = value?.fileList[0].originFileObj;
      // TranslationService.uploadTranslations(file);
    }
  };

  const preventRequest = () => false;
  const customRequest = (options: any) => {
    // options?.onSuccess?("ok")
  };

  const onFromValueInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setFromValueInput(event.target.value);
  };

  const onToValueInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setToValueInput(event.target.value);
  };

  const downloadTranslations = () => {
    const model: any = {
      attribute: attributeInput,
      groupCode: selectedTranslationGroupCode
        ? selectedTranslationGroupCode
        : "",
    };
    TranslationService.downloadTranslations(model).then((response: any) => {
      let url = window.URL.createObjectURL(new Blob([response.data]));
      saveAs(url, "translations.xlsx");
    });
  };

  const onUploadTranslation = () => {
    const model: any = {
      file: selectedTranslationsFile,
      groupCode: selectedTranslationGroupCode
        ? selectedTranslationGroupCode
        : "",
      attribute: attributeInput,
    };

    TranslationService.uploadTranslations(model)
      .then((resposne: any) => {
        setIsTableLoading(true);
        onClose();
        setAttributeInput("");
        setSelectedTranslationGroupCode(selectedGroupCode);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.UploadSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Translations upload succeeded.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const onSaveNewTranslation = () => {
    if (editedRow?.id) {
      const model: UpdateTranslationModel = {
        id: editedRow.id,
        GroupCode: selectedTranslationGroupCode,
        Attribute: attributeInput,
        FromValue: fromValueInput,
        ToValue: toValueInput,
      };
      TranslationService.updateTranslation(model)
        .then((response: any) => {
          setIsTableLoading(true);
          onClose();
          setAttributeInput("");
          setFromValueInput("");
          setToValueInput("");
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.UpdateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Translation edited successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    } else {
      const model: CreateTranslationModel = {
        GroupCode: selectedTranslationGroupCode,
        Attribute: attributeInput,
        FromValue: fromValueInput,
        ToValue: toValueInput,
      };
      TranslationService.createTranslation(model)
        .then((response: any) => {
          setSelectedAttribute(attributeInput);
          setIsTableLoading(true);
          onClose();
          setAttributeInput("");
          setFromValueInput("");
          setToValueInput("");
          NotificationsUtil.openNotificationDialog({
            description: t(
              "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.CreateSuccessDescription",
              {
                lngs: i18n.languages,
                defaultValue: "Translation created successfully.",
              }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
    }
  };

  const onDeleteTranslation = (id: number) => {
    TranslationService.deleteTranslation(id)
      .then((response: any) => {
        setIsTableLoading(true);
        NotificationsUtil.openNotificationDialog({
          description: t(
            "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.DeleteSuccessDescription",
            {
              lngs: i18n.languages,
              defaultValue: "Translation deleted successfully.",
            }
          )!,
          type: NOTIFICATION_TYPES.SUCCESS,
          logError: false,
        });
      })
      .catch((error: any) => {
        NotificationsUtil.openNotificationDialog({
          description: error.response.data,
          type: NOTIFICATION_TYPES.ERROR,
          logError: true,
        });
      });
  };

  const cancel = (e: React.MouseEvent<HTMLElement>) => {
    message.error("Click on No");
  };

  const onCopyTranslation = (record: any) => {
    showDrawer(RowAction.Create);
    setAttributeInput(record.attribute);
    setFromValueInput(record.fromValue);
    setToValueInput(record.toValue);
  };

  const onEditTranslation = (record: any) => {
    showDrawer(RowAction.Edit);
    setAttributeInput(record.attribute);
    setFromValueInput(record.fromValue);
    setToValueInput(record.toValue);
    setEditedRow(record);
  };

  const onGroupCodeChange = (value: string) => {
    setSelectedTranslationGroupCode(value);
  };

  function onlyUnique(value: any, index: any, self: any) {
    return self.indexOf(value) === index;
  }

  useEffect(() => {
    if (newTranslationDrawerEnabled) {
      showDrawer(RowAction.Create);
    }
  }, [newTranslationDrawerEnabled]);

  useEffect(() => {
    if (downloadTranslationDrawerEnabled) {
      showDrawer(RowAction.Download);
    }
  }, [downloadTranslationDrawerEnabled]);

  useEffect(() => {
    if (downloadTranslationTemplateDrawerEnabled) {
      const model: any = {
        groupCode: "downloadTemplate",
        attribute: "downloadTemplate",
      };
      TranslationService.downloadTranslations(model).then((response: any) => {
        let url = window.URL.createObjectURL(new Blob([response.data]));
        saveAs(url, "translations.xlsx");
        setDownloadTranslationTemplateDrawerEnabled(false);
      });
    }
  }, [downloadTranslationTemplateDrawerEnabled]);

  useEffect(() => {
    if (uploadTranslationDrawerEnabled) {
      showDrawer(RowAction.Upload);
    }
  }, [uploadTranslationDrawerEnabled]);

  useEffect(() => {
    if (isTableLoading) {
      TranslationService.getAllTranslation().then((response: any) => {
        const data = response.data;
        setAllTranslationAttributes(data);
        setTranslationAttributes(
          data
            .filter((x: any) =>
              selectedGroupCode
                ? x.groupCode === selectedGroupCode
                : true
            )
            .map((x: any) => selectedGroupCode ? x.attribute : `${x.groupCode ? x.groupCode + " - " : ""}${x.attribute}`)
            .filter(onlyUnique)
            .sort(function (a: any, b: any) {
              if (a?.toLowerCase().indexOf(" - ") !== -1 && b?.toLowerCase().indexOf(" - ") === -1) return 1;
              if (b?.toLowerCase().indexOf(" - ") !== -1 && a?.toLowerCase().indexOf(" - ") === -1) return -1;
              if (a?.toLowerCase() < b?.toLowerCase()) return -1;
              if (a?.toLowerCase() > b?.toLowerCase()) return 1;
              return 0;
            })
        );
        
        if (selectedGroupCode) {
          setTranslationKey(
              data.filter(
                  (x: any) =>
                      x.groupCode === selectedGroupCode && 
                      x.attribute === selectedAttribute
              ));
        } else {
          setTranslationKey(
              data.filter(
                  (x: any) =>
                      `${x.groupCode ? x.groupCode + " - " : ""}${x.attribute}` === selectedAttribute
              )
          );
        }
        
        setIsTableLoading(false);
      });
    }
  }, [isTableLoading, selectedGroupCode, selectedAttribute]);

  useEffect(() => {
    if (selectedAttribute) {
      const items = selectedAttribute?.split(" - ") ?? "";
      setAttributeInput(selectedGroupCode ? selectedAttribute : (items.length === 1 ? items[0] : items[1]));
    }
  }, [selectedAttribute]);

  useEffect(() => {
    setSelectedAttribute("");
    setIsTableLoading(true);
  }, [selectedGroupCode]);

  useEffect(() => {
    if (attributeInput && toValueInput && editedRow) {
      const def = translationKey.find((x: any) => x.id === editedRow.id);

      if (
        attributeInput !== def.attribute ||
        fromValueInput !== def.fromValue ||
        toValueInput !== def.toValue
      ) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [attributeInput, fromValueInput, toValueInput, editedRow, translationKey]);

  const handleTableChange = (value: string) => {
    setSelectedAttribute(value);
    setIsTableLoading(true);
    // setSelectedTable(tables.find((x: any) => x.id === value));
    // setSelectedField(undefined);
  };
  const handleTableMenuClick = (e: any) => {
    setSelectedAttribute(e.key);
    setIsTableLoading(true);
    // setSelectedTable(tables.find((x: any) => x.id.toString() === e.key));
    // setSelectedField(undefined);
  };

  const handleTranslationloadAttributeChange = (value: string) => {
    setAttributeInput(value);
    // setSelectedTable(tables.find((x: any) => x.id === value));
    // setSelectedField(undefined);
  };

  useEffect(() => {
    const newAttributes = allTranslationAttributes
      .filter((x: any) =>
        selectedTranslationGroupCode
          ? x.groupCode === selectedTranslationGroupCode
          : x.groupCode === null
      )
      .map((x: any) => selectedGroupCode ? x.attribute : `${x.groupCode ? x.groupCode + " - " : ""}${x.attribute}`)
      .filter(onlyUnique)
      .sort(function (a: any, b: any) {
        if (a?.toLowerCase().indexOf(" - ") !== -1 && b?.toLowerCase().indexOf(" - ") === -1) return 1;
        if (b?.toLowerCase().indexOf(" - ") !== -1 && a?.toLowerCase().indexOf(" - ") === -1) return -1;
        if (a?.toLowerCase() < b?.toLowerCase()) return -1;
        if (a?.toLowerCase() > b?.toLowerCase()) return 1;
        return 0;
      })

    setDrawerTranslationAttributes(newAttributes);
  }, [
    selectedTranslationGroupCode,
    translationAttributes,
    allTranslationAttributes,
  ]);

  const deleteTranslationKey = () => {
    const items = selectedAttribute?.split(" - ") ?? "";
    let groupCode = selectedGroupCode ? selectedGroupCode : (items.length === 2 ? items[0] : null);
    let attribute = selectedGroupCode ? selectedAttribute : (items.length === 1 ? items[0] : items[1]);

    TranslationService.deleteTranslations(groupCode, attribute)
        .then((response: any) => {
          setIsTableLoading(true);
          setSelectedAttribute("");
          NotificationsUtil.openNotificationDialog({
            description: t(
                "Page.Configuration.TableSetup.Tabs.Translations.Notifications.Translation.DeleteKeySuccess",
                {
                  lngs: i18n.languages,
                  defaultValue: "Translation key deleted successfully.",
                }
            )!,
            type: NOTIFICATION_TYPES.SUCCESS,
            logError: false,
          });
        })
        .catch((error: any) => {
          NotificationsUtil.openNotificationDialog({
            description: error.response.data,
            type: NOTIFICATION_TYPES.ERROR,
            logError: true,
          });
        });
  };
  
  const getAttributeLabel = (item: any) => {
    if (!item) {
      return "No key";
    }
    
    const items = item.split(" - ");
    
    if (items.length === 1) {
      return item;
    }
    
    if (items[0].length > 7) {
      return items[0].slice(0, 7) + "..." + " - " + items[1]
    }
    
    return item;
  }

  interface DataType {
    id: number;
    key: number;
    fromValue: string;
    toValue: string;
  }

  const columnsTable: ColumnsType<DataType> = [
    // {
    //   title: "Attribute",
    //   dataIndex: "attribute",
    //   key: "attribute",
    //   filterMode: "tree",
    //   filterSearch: true,
    // },
    {
      title: t(
        "Page.Configuration.TableSetup.Tabs.Translations.Table.Headers.FromValue",
        {
          lngs: i18n.languages,
          defaultValue: "From value:",
        }
      ),
      dataIndex: "fromValue",
      key: "fromValue",
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.fromValue.localeCompare(b.fromValue),
    },
    {
      title: t(
        "Page.Configuration.TableSetup.Tabs.Translations.Table.Headers.ToValue",
        {
          lngs: i18n.languages,
          defaultValue: "To value:",
        }
      ),
      dataIndex: "toValue",
      key: "toValue",
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.toValue.localeCompare(b.toValue),
    },
  ];

  const scroll = { y: "calc(70vh - 10em)" };

  return (
    <div className="table-setup-tab-container">
      <div className="section-container-sm">
        <div className="section-header">
          <Select
            className={styles.selectWidth}
            showSearch={true}
            showArrow={true}
            allowClear={true}
            value={selectedAttribute}
            onChange={handleTableChange}
            onClear={() => setSelectedAttribute("")}
            style={{ flex: 2 }}
            filterSort={(optionA, optionB) =>
                optionA.value! < optionB.value! ? -1 : 1
            }
            placeholder={t(
              "Page.Configuration.TableSetup.Tabs.Translations.Placeholders.SelectTranslationKey",
              {
                lngs: i18n.languages,
                defaultValue: "Select Translation key",
              }
            )}
            optionFilterProp="children"
            filterOption={(inputValue: any, option: any) =>
                option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
            }
          >
            {translationAttributes.map((item: any) => {
              return (
                <Option value={item ? item : "no-key"}>
                  {item ? item : "No key"}
                </Option>
              );
            })}
          </Select>
        </div>
        <Divider />
        <div className="section-items">
          {!isTableLoading && (
            <Menu
              items={translationAttributes.map((item: any) => {
                return {
                  key: item ? item : "no-key",
                  label: getAttributeLabel(item),
                };
              })}
              selectedKeys={[selectedAttribute]}
              onClick={handleTableMenuClick}
            />
          )}
          {isTableLoading && (
            <div className={styles.loadingContainer}>
              <Spin />
            </div>
          )}
        </div>
      </div>
      <div className="data-container">
        {selectedAttribute && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Popconfirm
                style={{ width: "400px" }}
                placement="topRight"
                title="Are you sure you want to delete selected Translation Key"
                icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                onConfirm={() => deleteTranslationKey()}
                // onCancel={() => cancel}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button size="small" danger icon={<DeleteOutlined />}>
                  Delete Translations Key
                </Button>
              </Popconfirm>
            </div>
            <Divider />
          </>
        )}

        {/* {!isTableLoading ? ( */}
        <>
          {/* <div className="title-actions">
            <div className="title-info">
              <span>Translations</span>
              <p>
                Contextual information frame with Lorem ipsum dolor sit amet,
                consectetur adipiscing elit. Proin sit amet auctor lacus, vel
                ornare lacus. Donec at aliquam purus. Vivamus non tincidunt
                ipsum, non ultrices orci.{" "}
              </p>
            </div>
            <div className="header-buttons">
              <Button
                icon={<PlusOutlined />}
                size="small"
                type="ghost"
                onClick={() => showDrawer(RowAction.Create)}
              >
                Add translation
              </Button>
              <Button
                // disabled
                icon={<DownloadOutlined />}
                size="small"
                type="ghost"
                onClick={() => downloadTranslations()}
                // className="update-btn"
              >
                Download translations
              </Button>
              <Button
                icon={<EditOutlined />}
                size="small"
                type="ghost"
                onClick={() => showDrawer(RowAction.Upload)}
                >
                  Upload translations
              </Button>

            </div>
          </div> */}
          <div className="table-container">
            {!selectedAttribute ? (
              <Empty
                description={
                  !selectedAttribute &&
                  t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Tutorial.GetStarted",
                    {
                      lngs: i18n.languages,
                      defaultValue:
                        "Select a Translation key on the left to get started",
                    }
                  )
                }
              />
            ) : (
              <Datatable
                scroll={scroll}
                tableColumns={columnsTable}
                data={translationKey.map((x:any) => ({
                  ...x,
                }))}
                setData={setTranslationKey}
                isTableLoading={isTableLoading}
                otherActions={[]}
                otherRowEnabled={false}
                editRowEnabled={true}
                copyRowEnabled={true}
                deleteRowEnabled={true}
                onEditFunc={onEditTranslation}
                onCopyFunc={onCopyTranslation}
                onDeleteFunc={onDeleteTranslation}
                onDoubleClickRow={onDoubleClickRow}
                selectable={false}
                multiSelect={false}
                selectedRows={[]}
                setSelectedRows={undefined}
                checkPermissions={true}
              ></Datatable>
            )}
          </div>
        </>
        <Drawer
          width={"400px"}
          getContainer={false}
          style={{ position: "absolute" }}
          title={drawerTitle}
          placement={DRAWER_PLACEMENTS.BOTTOM}
          onClose={onClose}
          visible={openDrawer}
          closable={false}
          extra={
            <Space>
              {RowAction?.View === drawerAction && (
                <>
                  <Button
                    onClick={() => showDrawer(RowAction.Edit)}
                    icon={<EditOutlined />}
                    size="small"
                    className={"active-btn"}
                  >
                    {t("General.Labels.Edit", {
                      lngs: i18n.languages,
                      defaultValue: "Edit",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Create === drawerAction && (
                <>
                  <Button
                    disabled={
                        !attributeInput || !toValueInput
                    }
                    className={
                      !attributeInput || !toValueInput
                        ? ""
                        : "active-btn"
                    }
                    onClick={onSaveNewTranslation}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Save", {
                      lngs: i18n.languages,
                      defaultValue: "Save",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Edit === drawerAction && (
                <>
                  <Button
                    disabled={!updateRowAllowed}
                    className={!updateRowAllowed ? "" : "active-btn"}
                    onClick={onSaveNewTranslation}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Update", {
                      lngs: i18n.languages,
                      defaultValue: "Update",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Download === drawerAction && (
                <>
                  <Button
                    disabled={!attributeInput}
                    className={!attributeInput ? "" : "active-btn"}
                    onClick={downloadTranslations}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.Download", {
                      lngs: i18n.languages,
                      defaultValue: "Download",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
              {RowAction?.Upload === drawerAction && (
                <>
                  <Button
                    disabled={!selectedTranslationsFile || !attributeInput}
                    className={
                      !selectedTranslationsFile || !attributeInput
                        ? ""
                        : "active-btn"
                    }
                    onClick={onUploadTranslation}
                    icon={<CheckOutlined />}
                    size="small"
                  >
                    {t("General.Labels.StartUpload", {
                      lngs: i18n.languages,
                      defaultValue: "Start upload",
                    })}
                  </Button>
                  <Button
                    type="ghost"
                    onClick={onClose}
                    icon={<CloseOutlined />}
                    size="small"
                  />
                </>
              )}
            </Space>
          }
        >
          {RowAction.View === drawerAction && (
            <Form
              disabled={RowAction.View === drawerAction ? true : false}
              layout="inline"
              // wrapperCol={{ span: 12 }}
              labelCol={{ span: 12 }}
              form={drawerForm}
            >
              <Form.Item
                name="groupCode"
                style={{ width: "300px" }}
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.GroupCode",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Group Code:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.GroupCode",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
              >
                <Select
                  placeholder={t("General.Placeholders.SelectOption", {
                    lngs: i18n.languages,
                    defaultValue: "Select option",
                  })}
                  loading={groupCodes.length === 0}
                  value={selectedTranslationGroupCode}
                  onChange={onGroupCodeChange}
                  showSearch={true}
                  showArrow={true}
                  allowClear={true}
                  options={groupCodes.map((x: any) => ({
                    label: x.code,
                    value: x.code,
                  }))}
                  filterSort={(optionA, optionB) =>
                      (optionA!.label as unknown as string)
                          .toLowerCase()
                          .localeCompare(
                              (optionB!.label as unknown as string).toLowerCase()
                          )
                  }
                ></Select>
              </Form.Item>
              <Form.Item
                name="attribute"
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.Attribute",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Attribute:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.Attribute",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
                style={{ width: "300px" }}
                required
              >
                <AutoComplete
                    showSearch={true}
                    showArrow={true}
                    allowClear={true}
                    value={attributeInput}
                    onChange={handleTranslationloadAttributeChange}
                    style={{ flex: 2 }}
                    filterSort={(optionA, optionB) =>
                        (optionA!.label as unknown as string)
                            .toLowerCase()
                            .localeCompare(
                                (optionB!.label as unknown as string).toLowerCase()
                            )
                    }
                    placeholder={t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })}
                    options={translationAttributes.map((x: any) => ({
                      value: x ? x : "no-key",
                      label: x ? x : "No key",
                    }))}
                    filterOption={(inputValue: any, option: any) =>
                        option?.value
                            .toUpperCase()
                            .indexOf(inputValue.toUpperCase()) !== -1
                    }
                ></AutoComplete>
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.FromValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "From value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.FromValue",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
              >
                <Input
                  value={fromValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onFromValueInputChange}
                />
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.ToValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "To value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.ToValue",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
                required
              >
                <Input
                  value={toValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onToValueInputChange}
                />
              </Form.Item>
            </Form>
          )}
          {(RowAction.Create === drawerAction ||
            RowAction.Edit === drawerAction) && (
            <Form
              // disabled={RowAction.Create === drawerAction ? true : false}
              layout="inline"
              // wrapperCol={{ span: 12 }}
              labelCol={{ span: 12 }}
              form={drawerForm}
            >
              <Form.Item
                name="groupCode"
                style={{ width: "300px" }}
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.GroupCode",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Group Code:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.GroupCode",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
              >
                <Select
                  placeholder={
                    t("General.Placeholders.SelectOption", {
                      lngs: i18n.languages,
                      defaultValue: "Select option",
                    })!
                  }
                  loading={groupCodes.length === 0}
                  value={selectedTranslationGroupCode}
                  onChange={onGroupCodeChange}
                  showSearch={true}
                  showArrow={true}
                  allowClear={true}
                  options={groupCodes.map((x: any) => ({
                    label: x.code,
                    value: x.code,
                  }))}
                  filterSort={(optionA, optionB) =>
                      (optionA!.label as unknown as string)
                          .toLowerCase()
                          .localeCompare(
                              (optionB!.label as unknown as string).toLowerCase()
                          )
                  }
                ></Select>
              </Form.Item>
              <Form.Item
                name="attribute"
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.Attribute",
                  {
                    lngs: i18n.languages,
                    defaultValue: "Attribute:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.Attribute",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
                style={{ width: "300px" }}
                required
              >
                <AutoComplete
                    showSearch={true}
                    showArrow={true}
                    allowClear={true}
                    value={attributeInput}
                    onChange={handleTranslationloadAttributeChange}
                    style={{ flex: 2 }}
                    placeholder={t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })}
                    options={translationAttributes.map((x: any) => ({
                      value: x ? x : "no-key",
                      label: x ? x : "No key",
                    }))}
                    filterOption={(inputValue: any, option: any) =>
                        option?.value
                            .toUpperCase()
                            .indexOf(inputValue.toUpperCase()) !== -1
                    }
                ></AutoComplete>
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.FromValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "From value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.FromValue",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
              >
                <Input
                  value={fromValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onFromValueInputChange}
                />
              </Form.Item>
              <Form.Item
                label={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Labels.ToValue",
                  {
                    lngs: i18n.languages,
                    defaultValue: "To value:",
                  }
                )}
                tooltip={t(
                  "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.ToValue",
                  { lngs: i18n.languages, defaultValue: "" }
                )}
                required
              >
                <Input
                  value={toValueInput}
                  placeholder={
                    t("General.Placeholders.EnterValue", {
                      lngs: i18n.languages,
                      defaultValue: "Enter value",
                    })!
                  }
                  onChange={onToValueInputChange}
                />
              </Form.Item>
            </Form>
          )}
          {(RowAction.Upload === drawerAction ||
            RowAction.Download === drawerAction) && (
            <Form
              // disabled={RowAction.Upload === drawerAction ? true : false}
              layout="vertical"
              // wrapperCol={{ span: 12 }}
              labelCol={{ span: 12 }}
              form={drawerForm}
            >
              <div className="translation_top_form">
                <Form.Item
                  name="groupCode"
                  style={{ width: "300px" }}
                  label={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Labels.GroupCode",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Group Code:",
                    }
                  )}
                  tooltip={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.GroupCode",
                    { lngs: i18n.languages, defaultValue: "" }
                  )}
                >
                  <Select
                    placeholder={t(
                      "Page.Configuration.TableSetup.Tabs.Translations.Labels.GroupCode",
                      {
                        lngs: i18n.languages,
                        defaultValue: "Group code",
                      }
                    )}
                    loading={groupCodes.length === 0}
                    value={selectedTranslationGroupCode}
                    onChange={onGroupCodeChange}
                    showSearch={true}
                    showArrow={true}
                    allowClear={true}
                    options={groupCodes.map((x: any) => ({
                      label: x.code,
                      value: x.code,
                    }))}
                    filterSort={(optionA, optionB) =>
                        (optionA!.label as unknown as string)
                            .toLowerCase()
                            .localeCompare(
                                (optionB!.label as unknown as string).toLowerCase()
                            )
                    }
                  ></Select>
                </Form.Item>
                <Form.Item
                  name="attribute"
                  label={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Labels.Attribute",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Attribute:",
                    }
                  )}
                  tooltip={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.Attribute",
                    { lngs: i18n.languages, defaultValue: "" }
                  )}
                  style={{ width: "300px" }}
                >
                  <AutoComplete
                      showSearch={true}
                      showArrow={true}
                      allowClear={true}
                      value={attributeInput}
                      onChange={handleTranslationloadAttributeChange}
                      style={{ flex: 2 }}
                      placeholder={t("General.Placeholders.EnterValue", {
                        lngs: i18n.languages,
                        defaultValue: "Enter value",
                      })}
                      options={translationAttributes.map((x: any) => ({
                        value: x ? x : "no-key",
                        label: x ? x : "No key",
                      }))}
                      filterOption={(inputValue: any, option: any) =>
                          option?.value
                              .toUpperCase()
                              .indexOf(inputValue.toUpperCase()) !== -1
                      }
                  ></AutoComplete>
                </Form.Item>
              </div>

              {RowAction.Upload === drawerAction && (
                <Form.Item
                  label={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Labels.Translations",
                    {
                      lngs: i18n.languages,
                      defaultValue: "Translations:",
                    }
                  )}
                  tooltip={t(
                    "Page.Configuration.TableSetup.Tabs.Translations.Tooltips.Translations",
                    { lngs: i18n.languages, defaultValue: "" }
                  )}
                >
                  <Upload
                    fileList={
                      selectedTranslationsFile ? [selectedTranslationsFile] : []
                    }
                    onChange={handleTranslationsFileChange}
                    beforeUpload={preventRequest}
                    customRequest={customRequest}
                    accept=".xls,.xlsx"
                  >
                    <Button icon={<EditOutlined />} size="small" type="ghost">
                      {" "}
                      {t("General.Labels.Upload", {
                        lngs: i18n.languages,
                        defaultValue: "Upload",
                      })}
                    </Button>
                  </Upload>
                </Form.Item>
              )}
            </Form>
          )}
        </Drawer>
        {/* ) : (
        <div className="empty-container">
          <Empty description={false} />
        </div>
        )} */}
      </div>
    </div>
  );
};

export default TableSetup;
